<template>
  <span
    ref="trigger"
    class="ui-tooltip-trigger"
    @focusin="scheduleOpen"
    @focusout="close"
    @mouseenter="scheduleOpen"
    @mouseleave="close"
  >
    <slot name="trigger" />
  </span>
  <Teleport to="body">
    <span ref="tooltip" class="ui-tooltip-positioner">
      <transition name="ui-tooltip" @after-leave="destroyPopper">
        <span v-if="open && !disabled" :id="tooltipId" class="ui-tooltip" role="tooltip">
          <span v-if="showArrow" ref="arrow" class="ui-tooltip__arrow" data-popper-arrow>
            <svg aria-hidden="true" height="8" viewBox="0 0 8 8" width="8">
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </span>
          <div class="ui-tooltip-content">
            <slot />
          </div>
        </span>
      </transition>
    </span>
  </Teleport>
</template>
<script setup lang="ts">
  import { createPopper, type Instance, type Placement } from '@popperjs/core'
  import { nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue'

  defineOptions({ name: 'UiTooltip' })

  const props = withDefaults(
    defineProps<{
      delay?: number
      disabled?: boolean
      placement?: Placement
      showArrow?: boolean
    }>(),
    { delay: 300, placement: 'top', showArrow: true },
  )
  const open = ref(false)
  const trigger = ref<HTMLElement>()
  const tooltip = ref<HTMLElement>()
  const arrow = ref<HTMLElement>()
  const tooltipId = `ui-tooltip-${useId()}`
  let timer: number | undefined
  let popper: Instance | undefined
  const triggerElement = () => trigger.value?.firstElementChild ?? trigger.value
  let describedElement: Element | undefined
  let previousDescription: string | null = null
  const connectDescription = (element: Element) => {
    describedElement = element
    previousDescription = element.getAttribute('aria-describedby')
    const ids = new Set(previousDescription?.split(/\s+/).filter(Boolean))
    ids.add(tooltipId)
    element.setAttribute('aria-describedby', [...ids].join(' '))
  }
  const disconnectDescription = () => {
    if (!describedElement) return
    if (previousDescription) describedElement.setAttribute('aria-describedby', previousDescription)
    else describedElement.removeAttribute('aria-describedby')
    describedElement = undefined
    previousDescription = null
  }
  const destroyPopper = () => {
    popper?.destroy()
    popper = undefined
  }
  const position = async () => {
    await nextTick()
    const reference = triggerElement()
    if (!reference || !tooltip.value) return
    connectDescription(reference)
    destroyPopper()
    popper = createPopper(reference, tooltip.value, {
      modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'arrow', options: { element: arrow.value } },
      ],
      placement: props.placement,
      strategy: 'fixed',
    })
  }
  const scheduleOpen = () => {
    if (props.disabled) return
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      open.value = true
      void position()
    }, props.delay)
  }
  const close = () => {
    window.clearTimeout(timer)
    open.value = false
    disconnectDescription()
  }
  const escape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close()
  }
  onMounted(() => {
    document.addEventListener('keydown', escape)
  })
  onBeforeUnmount(() => {
    document.removeEventListener('keydown', escape)
    close()
    destroyPopper()
  })
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

.ui-tooltip-trigger {
  display: contents;
}

.ui-tooltip-positioner {
 display: block;
 left: 0;
 pointer-events: none;
 position: fixed;
 top: 0;
 transition: none;
 width: max-content;
 z-index: 700;
}

.ui-tooltip-positioner:not([data-popper-placement]) {
 visibility: hidden;
}

.ui-tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ui-tooltip {
 @include typography.font-style('text-xs', 'semibold');

 background: var(--color-background-bg-primary-solid);
 border: 1px solid var(--color-background-bg-primary-solid);
 border-radius: var(--radius-md);
 color: var(--color-base-white);
 display: block;
 max-width: 400px;
 padding: 8px 12px;
 will-change: opacity, transform;
 width: max-content;

}


.ui-tooltip__arrow {
 height: 8px;
 position: absolute;
 width: 8px;

}

.ui-tooltip__arrow svg {
 display: block;
 fill: var(--color-background-bg-primary-solid);
 stroke: var(--color-background-bg-primary-solid);
}

.ui-tooltip-positioner[data-popper-placement^='top'] .ui-tooltip__arrow {
 bottom: -7px;

}

.ui-tooltip-positioner[data-popper-placement^='bottom'] .ui-tooltip__arrow {
 top: -7px;

}

.ui-tooltip-positioner[data-popper-placement^='bottom'] .ui-tooltip__arrow svg {
 transform: rotate(180deg);
}

.ui-tooltip-positioner[data-popper-placement^='left'] .ui-tooltip__arrow {
 right: -7px;

}

.ui-tooltip-positioner[data-popper-placement^='left'] .ui-tooltip__arrow svg {
 transform: rotate(-90deg);
}

.ui-tooltip-positioner[data-popper-placement^='right'] .ui-tooltip__arrow {
 left: -7px;

}

.ui-tooltip-positioner[data-popper-placement^='right'] .ui-tooltip__arrow svg {
 transform: rotate(90deg);

}


.ui-tooltip-enter-active {
 transition: opacity 0.2s ease-out, transform 0.2s ease-out;

}

.ui-tooltip-leave-active {
 transition: opacity 0.15s ease-in, transform 0.15s ease-in;

}


.ui-tooltip-enter-from,
.ui-tooltip-leave-to {
 opacity: 0;

}

.ui-tooltip-positioner[data-popper-placement^='top'] .ui-tooltip-enter-from,
.ui-tooltip-positioner[data-popper-placement^='top'] .ui-tooltip-leave-to {
 transform: translateY(2px);
}

.ui-tooltip-positioner[data-popper-placement^='bottom'] .ui-tooltip-enter-from,
.ui-tooltip-positioner[data-popper-placement^='bottom'] .ui-tooltip-leave-to {
 transform: translateY(-2px);
}

.ui-tooltip-positioner[data-popper-placement^='left'] .ui-tooltip-enter-from,
.ui-tooltip-positioner[data-popper-placement^='left'] .ui-tooltip-leave-to {
 transform: translateX(2px);
}

.ui-tooltip-positioner[data-popper-placement^='right'] .ui-tooltip-enter-from,
.ui-tooltip-positioner[data-popper-placement^='right'] .ui-tooltip-leave-to {
 transform: translateX(-2px);

}

</style>
